import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/common/Header';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import { deleteStorePhoto, getStorePhotos, uploadStorePhotos } from '../services/adminPhotoService';

const MAX_TOTAL_PHOTOS = 6;
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/jpg,image/webp';

const StorePhotoManagement = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStorePhotos(storeId);
      setPhotos(res?.data?.photos || []);
    } catch (err) {
      showSnackbar(err.message || '매장 사진을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [storeId, showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const remaining = MAX_TOTAL_PHOTOS - photos.length;
    if (files.length > remaining) {
      showSnackbar(`최대 ${MAX_TOTAL_PHOTOS}장까지 가능합니다. (남은 슬롯 ${remaining})`, 'error');
      return;
    }
    upload(files);
  };

  const upload = async (files) => {
    setUploading(true);
    try {
      await uploadStorePhotos(storeId, files);
      showSnackbar(`${files.length}장 업로드 완료`, 'success');
      await load();
    } catch (err) {
      showSnackbar(err.message || '업로드에 실패했습니다.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm('이 사진을 삭제하시겠습니까?')) return;
    setDeletingId(photoId);
    try {
      await deleteStorePhoto(storeId, photoId);
      showSnackbar('삭제 완료', 'success');
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      showSnackbar(err.message || '삭제에 실패했습니다.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-layout">
      <Header />
      <div style={{ padding: '16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ border: '1px solid #ccc', background: '#fff', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}
          >
            ← 뒤로
          </button>
          <h2 style={{ margin: 0, fontSize: 18 }}>매장 사진 관리</h2>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#666' }}>
            {photos.length} / {MAX_TOTAL_PHOTOS}장
          </span>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || photos.length >= MAX_TOTAL_PHOTOS}
            style={{
              background: photos.length >= MAX_TOTAL_PHOTOS ? '#ccc' : '#F85009',
              color: '#fff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: photos.length >= MAX_TOTAL_PHOTOS ? 'not-allowed' : 'pointer',
            }}
          >
            {uploading ? '업로드 중...' : '+ 사진 추가'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            onChange={onPickFiles}
            style={{ display: 'none' }}
          />
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>로딩 중...</div>
        ) : photos.length === 0 ? (
          <div
            style={{
              padding: 60,
              textAlign: 'center',
              border: '2px dashed #ccc',
              borderRadius: 8,
              color: '#888',
            }}
          >
            등록된 사진이 없습니다. "+ 사진 추가" 로 업로드해주세요.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
            }}
          >
            {photos
              .slice()
              .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
              .map((p) => (
                <div
                  key={p.id}
                  style={{
                    position: 'relative',
                    border: '1px solid #e6e6e6',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#fff',
                  }}
                >
                  <div style={{ position: 'relative', paddingBottom: '75%', background: '#f3f3f3' }}>
                    <img
                      src={p.url}
                      alt={p.id}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 12,
                      color: '#666',
                    }}
                  >
                    <span>순서 #{(p.displayOrder ?? 0) + 1}</span>
                    <button
                      type="button"
                      onClick={() => remove(p.id)}
                      disabled={deletingId === p.id}
                      style={{
                        border: '1px solid #F85009',
                        background: '#fff',
                        color: '#F85009',
                        padding: '4px 10px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {deletingId === p.id ? '삭제 중...' : '삭제'}
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
      <Snackbar message={snackbar.message} type={snackbar.type} isOpen={snackbar.isOpen} onClose={hideSnackbar} />
    </div>
  );
};

export default StorePhotoManagement;